import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "../../../hooks/useTranslation";
import { API_URL } from "../../../providers/dataProvider";
import { CopyIconButton } from "./CopyIconButton";
import {
  clearRequestLog,
  isFailed,
  LoggedRequest,
  toCurl,
  useRequestLog,
} from "./requestLog";

// "14:05:09": the UTC time, as the backend logs it
const utcTime = (at: number) => new Date(at).toISOString().slice(11, 19);

// the address without the API's own part, so the path reads first
const pathOf = (url: string) =>
  url.startsWith(API_URL) ? url.slice(API_URL.length) || "/" : url;

const statusColor = (entry: LoggedRequest) => {
  if (entry.status === null || entry.status >= 500) return "error.main";
  if (entry.status >= 400) return "warning.main";
  return "success.main";
};

type Props = { open: boolean; onClose: () => void };

// The panel's last requests to the backend (developer tool, test environment
// only): UTC time, method, path, status, duration, the backend's message of a
// failed one, and each as cURL to replay it.
export const RequestLogDrawer = ({ open, onClose }: Props) => {
  const { translate } = useTranslation("pages.admin.header.requestLog");
  const entries = useRequestLog();
  const [onlyFailed, setOnlyFailed] = useState(false);
  const shown = onlyFailed ? entries.filter(isFailed) : entries;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Stack
        gap={2}
        sx={{ width: { xs: "100vw", sm: "min(760px, 100vw)" }, p: 2.5 }}
      >
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
            {translate("title")}
          </Typography>
          <IconButton aria-label={translate("close")} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {translate("description", { count: 50 })}
        </Typography>
        <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={onlyFailed}
                onChange={(_event, on) => setOnlyFailed(on)}
              />
            }
            label={
              <Typography variant="body2">
                {translate("onlyFailed", {
                  count: entries.filter(isFailed).length,
                })}
              </Typography>
            }
          />
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            disabled={entries.length === 0}
            onClick={clearRequestLog}
          >
            {translate("clear")}
          </Button>
        </Stack>

        {shown.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {translate(onlyFailed ? "noFailed" : "empty")}
          </Typography>
        ) : (
          <Stack
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            {shown.map((entry, index) => (
              <Stack
                key={entry.id}
                gap={0.5}
                sx={{
                  px: 1.5,
                  py: 1,
                  borderTop: index > 0 ? "1px solid" : "none",
                  borderColor: "divider",
                  bgcolor: (theme) =>
                    isFailed(entry)
                      ? alpha(theme.palette.error.main, 0.06)
                      : "transparent",
                }}
              >
                <Stack direction="row" alignItems="center" gap={1.25}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontVariantNumeric: "tabular-nums", minWidth: 58 }}
                  >
                    {utcTime(entry.at)}
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ fontFamily: "monospace", minWidth: 48 }}
                  >
                    {entry.method}
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ color: statusColor(entry), minWidth: 30 }}
                  >
                    {entry.status ?? translate("noAnswer")}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontFamily: "monospace",
                      fontSize: 12.5,
                      wordBreak: "break-all",
                    }}
                  >
                    {pathOf(entry.url)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      whiteSpace: "nowrap",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {entry.durationMs === null
                      ? ""
                      : translate("duration", { ms: entry.durationMs })}
                  </Typography>
                  <CopyIconButton
                    value={toCurl(entry)}
                    label={translate("copyCurl")}
                  />
                </Stack>
                {entry.message && isFailed(entry) && (
                  <Typography
                    variant="caption"
                    color="error.main"
                    sx={{ pl: 0.25 }}
                  >
                    {entry.message}
                  </Typography>
                )}
              </Stack>
            ))}
          </Stack>
        )}
        <Typography variant="caption" color="text.secondary">
          {translate("curlHint")}
        </Typography>
      </Stack>
    </Drawer>
  );
};
